import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";

import M3Base from "../../Base/M3Base";
import TabPanel from "../../../../UI/TabPanel/TabPanel";
import DroneMap from "../../Components/DroneMap/DroneMap";
import OSGridMap from "../../Components/OSGridMap/OSGridMap";
import ErrorBoard from "../../../../UI/ErrorBoard/ErrorBoard";
import TabsComponent from "../../../../UI/TabsComponent/TabsComponent";
import MapGuessHistory from "../../Components/MapGuessHistory/MapGuessHistory";
import M3RookieSection from "../../Components/M3RookieSection/M3RookieSection";
import RookieCoordinateInput from "../../Components/RookieCoordinateInput/RookieCoordinateInput";

import { pinMultipleClues } from "../../../../../redux/slices/pinclueSlice";
import { getMissionAccomplishmentsThunk } from "../../../../../redux/slices/createUserSlice";

import { useFeedback } from "../../../../../hooks/useFeedback";
import { useShowReward } from "../../../../../hooks/useShowReward";

import bg from "../../../../../assets/images/m3-bg.png";

import { getHint } from "../../../../../utils/hint";
import { getInstructionList } from "../../../../../utils/OnScreenInstructions";

import { csvEscape } from "../../../../../helper/csvHelper";
import { forceSyncNow } from "../../../../../helper/forceSync";

import { m3RookieD, m3RookieR } from "../../../../../data/clueData";

import {
  fetchMission3RookieData,
  submitMission3Rookie,
} from "../../../../../api/MissionApis/mission3Api";

import {
  RookieStageClearData,
  RookieStageFailData,
  clueData,
} from "../../constants/rookieConstants";

import "./M3Rookie.scss";

const hintList = getHint(3, "Rookie");
const onScreenInstruction = getInstructionList(3, "Rookie");
const Assets = [bg, m3RookieD, m3RookieR];
const STORAGE_KEY = "m3_rookie_state_v1";

const M3Rookie = () => {
  const { user } = useSelector((state) => state.createUser);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [marker, setMarker] = useState({});
  const [packets, setPackets] = useState([]);
  const [attempts, setAttempts] = useState(5);
  const [latitude, setLatitude] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [longitude, setLongitude] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useFeedback(1400);
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [guessHistory, setGuessHistory] = useState(() => {
    // Load from localStorage on first render
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  });
  const [selectedPacket, setSelectedPacket] = useState(null);

  const [showReward, setShowReward, clearShowReward] = useShowReward(
    "m3RookieShowReward",
    false
  );

  const [mapCoordinates, setMapCoordinates] = useState({
    lat: 51.4925,
    lng: -0.1428,
  });

  const handlePacketSelect = useCallback(
    (packetNo) => {
      if (!Array.isArray(packets) || !packetNo) return;

      const packet = packets.find((p) => p.no === packetNo);
      if (!packet) return;

      setSelectedPacket(packet);

      // Combine all data lines and parse once
      const joined = Array.isArray(packet.data)
        ? packet.data.join(" ")
        : String(packet.data ?? "");
      const latMatch = joined.match(/LAT:\s*(-?\d+(?:\.\d+)?)/i);
      const lngMatch = joined.match(/LNG:\s*(-?\d+(?:\.\d+)?)/i);

      if (latMatch && lngMatch) {
        const lat = parseFloat(latMatch[1]);
        const lng = parseFloat(lngMatch[1]);
        if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
          setMapCoordinates({ lat, lng });
        }
      }
    },
    [packets]
  );

  // Function to check if the dropped marker matches the selected packet's coordinates
  const checkLatAndLng = async () => {
    try {
      if (!selectedPacket || !marker || !marker.lat || !marker.lng) {
        setFeedback("No selected packet or marker to check.", "info");
        return;
      }

      setIsLoading(true);

      const response = await submitMission3Rookie(
        user.systemName,
        user.authToken,
        marker
      );

      const newGuess = {
        coordinates: {
          lat: marker.lat.toFixed(4),
          lng: marker.lng.toFixed(4),
        },
        success: response?.success || false,
        timestamp: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        feedback: response?.feedback || response?.error || "Unknown error",
      };

      // Add new guess to history
      setGuessHistory((prev) => [...prev, newGuess]);

      if (response?.success) {
        setFeedback("Correct! Location identified.", "success");
        setTimeout(() => {
          setShowReward(true);
        }, 1500);
      } else {
        setAttempts((pre) => pre - 1);
        setFeedback(
          response?.error || "Incorrect. Revisit the GPS log.",
          "error"
        );
        setTimeout(() => {
          setMarker({});
        }, 500);

        setLatitude("");
        setLongitude("");
      }
    } catch (error) {
      console.error("Error in checkLatAndLng:", error);
      setFeedback("Something went wrong while checking coordinates.", "error");
    } finally {
      dispatch(
        getMissionAccomplishmentsThunk({
          authToken: user.authToken,
          systemName: user.systemName,
        })
      );
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (attempts < 1) {
      navigate("/missionFailed", {
        state: { stageFailData: RookieStageFailData },
      });
    }
  }, [attempts, navigate]);

  const getPackets = async () => {
    try {
      if (user?.authToken && user?.systemName) {
        setLoading(true); // Start loading only if the condition is met

        const packets = await fetchMission3RookieData({
          authToken: user.authToken,
          systemName: user.systemName,
        });
        setPackets(packets);
      }
      return []; // Return empty array if no user data
    } catch (error) {
      console.error("Error in rookie loader:", error);
      return []; // Return empty array on error
    } finally {
      setLoading(false); // Ensure loading is stopped in all cases
    }
  };

  // NEW: CSV builder + trigger
  const handleDownloadPackets = () => {
    try {
      const fileName = `rookie_data_${new Date()
        .toISOString()
        .replace(/[:.]/g, "-")}.csv`;

      // Define the CSV columns you want
      const headers = [
        "Packet Number",
        "Time",
        "Src",
        "Dst",
        "Proto",
        "Length",
        "Data",
      ];

      // Build rows
      const lines = [];
      lines.push(headers.join(",")); // header line

      // Assuming "packets" is your in-memory list
      (packets || []).forEach((p) => {
        const row = [
          csvEscape(p.no),
          csvEscape(p.time),
          csvEscape(p.src),
          csvEscape(p.dst),
          csvEscape(p.proto),
          csvEscape(p.length),
          csvEscape(p.data),
        ];
        lines.push(row.join(","));
      });

      const csv = lines.join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      setDownloadComplete(true);
      setFeedback("Packet CSV downloaded.", "success");

      // (Optional) auto-reset the “done” icon after 2.5s
      setTimeout(() => setDownloadComplete(false), 2500);

      // IMPORTANT: return true so TopRightIcons clears the active class
      return true;
    } catch (e) {
      console.error("Download error:", e);
      setFeedback("Failed to download packet CSV.", "error");
      // still return false so caller can decide (we clear active anyway)
      return false;
    }
  };

  const handlePinClueBoard = () => {
    dispatch(
      pinMultipleClues({
        missionId: "mission-3",
        clues: [
          { clueId: "clue-13", level: "rookie" },
          { clueId: "clue-14", level: "rookie" },
        ],
      })
    );

    navigate("/clueboard");
  };

  const onContinue = () => {
    dispatch(
      pinMultipleClues({
        missionId: "mission-3",
        clues: [
          { clueId: "clue-13", level: "rookie" },
          { clueId: "clue-14", level: "rookie" },
        ],
      })
    );
    setShowReward(false);
    clearShowReward();
    navigate("/missionComplete", {
      state: { stageClearData: RookieStageClearData },
    });
  };

  useEffect(() => {
    getPackets();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(guessHistory));
  }, [guessHistory]);

  const TABS = ["Map", "OS Grid", "Guess History"];
  const handleTabChange = React.useCallback((_, v) => setActiveTab(v), []);
  const flex1 = React.useMemo(() => ({ flex: 1 }), []);

  const handleLatChange = (val) => {
    const num = parseFloat(val);

    setLatitude(val);

    if (val === "") {
      setFeedback("");
      return;
    }

    if (num < -90 || num > 90) {
      setFeedback("Latitude must be between -90 and 90.");
    } else {
      setFeedback("");
    }
  };

  const handleLngChange = (val) => {
    const num = parseFloat(val);

    setLongitude(val);

    if (val === "") {
      setFeedback("");
      return;
    }

    if (num < -180 || num > 180) {
      setFeedback("Longitude must be between -180 and 180.");
    } else {
      setFeedback("");
    }
  };

  const addMarker = () => {
    if (latitude && longitude) {
      // Convert to numbers for validation
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);

      // Check if values are within valid ranges
      if (isNaN(lat) || lat < -90 || lat > 90) {
        setFeedback("Invalid latitude: must be between -90 and 90");
        return;
      }

      if (isNaN(lng) || lng < -180 || lng > 180) {
        setFeedback("Invalid longitude: must be between -180 and 180");
        return;
      }

      try {
        setMarker(
          { lat, lng } // Use the converted number values
        );
        const newGuess = {
          coordinates: {
            lat,
            lng,
          },
        };
        setGuessHistory((prev) => [...prev, newGuess]);
        setLatitude("");
        setLongitude("");
      } catch (e) {
        console.error("Error adding marker:", e);
      }
    } else {
      setFeedback("Latitude and longitude are required");
    }
  };

  useEffect(() => {
    if (!marker) return; // guard

    setLatitude(marker?.lat?.toFixed(4).toString());
    setLongitude(marker?.lng?.toFixed(4).toString());
  }, [marker]);

  return (
    <M3Base
      assets={Assets}
      loading={loading}
      clueData={clueData}
      hintList={hintList}
      isShowDownload={true}
      showReward={showReward}
      challengeName={"Rookie"}
      handleContinue={onContinue}
      setShowReward={setShowReward}
      handlePinClue={handlePinClueBoard}
      Instructions={onScreenInstruction}
      onDownload={handleDownloadPackets}
      isDownloadComplete={downloadComplete}
      handleSaveMission={forceSyncNow("m3-rookie-manual-save")}
    >
      <Stack useFlexGap gap={4} marginTop={"20px"}>
        <Stack>
          <ErrorBoard
            message={feedback.message}
            severity={feedback.severity}
            visible={!!feedback.message}
          />
        </Stack>
        <Stack useFlexGap gap={2} className="m3-rookie-container">
          <TabsComponent
            activeTab={activeTab}
            handleTabChange={handleTabChange}
            tabs={TABS}
          />
          <TabPanel value={activeTab} index={0} style={flex1}>
            <Stack direction={"row"} useFlexGap gap={2}>
              <Stack width={"70%"} useFlexGap gap={2}>
                <DroneMap
                  level="rookie"
                  marker={marker}
                  setMarker={setMarker}
                  lat={mapCoordinates.lat}
                  lng={mapCoordinates.lng}
                  flag="single"
                />
                <RookieCoordinateInput
                  latitude={latitude}
                  longitude={longitude}
                  addMarker={addMarker}
                  handleLatChange={handleLatChange}
                  handleLngChange={handleLngChange}
                />
              </Stack>
              <M3RookieSection
                marker={marker}
                packets={packets}
                isLoading={isLoading}
                checkFunction={checkLatAndLng}
                selectedPacket={selectedPacket}
                onPacketSelect={handlePacketSelect}
              />
            </Stack>
          </TabPanel>
          <TabPanel value={activeTab} index={1} style={{ flex: 1 }}>
            <Stack width={"100%"} height={"396px"}>
              <OSGridMap
                level="rookie"
                marker={marker}
                setMarker={setMarker}
                lat={mapCoordinates.lat}
                lng={mapCoordinates.lng}
              />
            </Stack>
          </TabPanel>
          <TabPanel value={activeTab} index={2} style={{ flex: 1 }}>
            <MapGuessHistory guessHistory={guessHistory} height="396" />
          </TabPanel>
        </Stack>
      </Stack>
    </M3Base>
  );
};

export default M3Rookie;
